import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import UiIcon from "../UiIcon";

// Presentation-only building blocks for the Admin Panel. They hold no business
// logic and never fetch data; pages pass in values and callbacks unchanged.

export function AdminPage({ children, className = "" }) {
    return <div className={`adm-page ${className}`.trim()}>{children}</div>;
}

export function AdminPageHeader({ eyebrow, title, description, actions, meta }) {
    return <header className="adm-page-header">
        <div className="adm-page-header-copy">
            {eyebrow && <div className="adm-eyebrow">{eyebrow}</div>}
            <h1 className="adm-title">{title}</h1>
            {description && <p className="adm-desc">{description}</p>}
        </div>
        {(actions || meta) && <div className="adm-header-actions">{meta}{actions}</div>}
    </header>;
}

export function AdminPanel({ title, description, actions, children, footer, flush = false, plainHead = false, className = "", id, as: Tag = "section" }) {
    const headingId = useId();
    return <Tag className={`adm-panel ${className}`.trim()} id={id} aria-labelledby={title ? headingId : undefined}>
        {(title || actions) && <div className={`adm-panel-head${plainHead ? " is-plain" : ""}`}>
            <div style={{ minWidth: 0 }}>
                {title && <h2 className="adm-panel-title" id={headingId}>{title}</h2>}
                {description && <p className="adm-panel-desc">{description}</p>}
            </div>
            {actions && <div className="adm-panel-actions">{actions}</div>}
        </div>}
        <div className={`adm-panel-body${flush ? " is-flush" : ""}`}>{children}</div>
        {footer && <div className="adm-panel-foot">{footer}</div>}
    </Tag>;
}

export function AdminStat({ label, value, helper, icon, tone, onClick, ariaLabel }) {
    const className = `adm-stat${tone ? ` is-${tone}` : ""}`;
    const content = <>
        <span className="adm-stat-label"><span>{label}</span>{icon && <span className="adm-stat-icon" aria-hidden="true"><UiIcon name={icon} size={15} /></span>}</span>
        <span className="adm-stat-value">{value}</span>
        {helper && <span className="adm-stat-helper" title={typeof helper === "string" ? helper : undefined}>{helper}</span>}
    </>;
    if (onClick) return <button type="button" className={className} onClick={onClick} aria-label={ariaLabel}>{content}</button>;
    return <div className={className}>{content}</div>;
}

export function AdminBadge({ tone = "neutral", children, plain = false, title }) {
    return <span className={`adm-badge adm-badge--${tone}${plain ? " is-plain" : ""}`} title={title}>{children}</span>;
}

export function AdminTabs({ items, value, onChange, ariaLabel, idPrefix }) {
    const buttons = useRef([]);
    const onKeyDown = (event, index) => {
        if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(event.key)) return;
        event.preventDefault();
        const last = items.length - 1;
        const rtl = document?.documentElement?.dir === "rtl";
        const forward = rtl ? "ArrowLeft" : "ArrowRight";
        const next = event.key === "Home" ? 0 : event.key === "End" ? last
            : event.key === forward ? (index === last ? 0 : index + 1) : (index === 0 ? last : index - 1);
        onChange(items[next].id);
        buttons.current[next]?.focus();
    };
    return <div className="adm-tabs" role="tablist" aria-label={ariaLabel}>
        {items.map((item, index) => {
            const selected = item.id === value;
            return <button key={item.id} ref={node => { buttons.current[index] = node; }} type="button" role="tab"
                id={idPrefix ? `${idPrefix}-tab-${item.id}` : undefined}
                aria-selected={selected} tabIndex={selected ? 0 : -1} className="adm-tab"
                onClick={() => onChange(item.id)} onKeyDown={event => onKeyDown(event, index)}>
                {item.icon && <UiIcon name={item.icon} size={15} />}
                <span>{item.label}</span>
                {item.count != null && <span className="adm-count">{item.count}</span>}
            </button>;
        })}
    </div>;
}

export function AdminSegmented({ options, value, onChange, ariaLabel }) {
    return <div className="adm-segmented" role="radiogroup" aria-label={ariaLabel}>
        {options.map(option => <button key={option.value} type="button" role="radio" aria-checked={option.value === value}
            onClick={() => onChange(option.value)}>{option.label}</button>)}
    </div>;
}

export function AdminField({ label, hint, required = false, children, wide = false, className = "" }) {
    return <label className={`adm-field${wide ? " is-wide" : ""} ${className}`.trim()}>
        <span>{label}{required && <span className="adm-required" aria-hidden="true">*</span>}</span>
        {children}
        {hint && <span className="adm-hint">{hint}</span>}
    </label>;
}

export function AdminSwitch({ checked, onChange, label, disabled = false, ariaLabel }) {
    return <label className="adm-switch">
        <input type="checkbox" role="switch" checked={Boolean(checked)} disabled={disabled}
            aria-checked={Boolean(checked)} aria-label={ariaLabel}
            onChange={event => onChange(event.target.checked)} />
        {label && <span>{label}</span>}
    </label>;
}

export function AdminEmpty({ icon = "activity", title, description, action, compact = false }) {
    return <div className={`adm-empty${compact ? " is-compact" : ""}`}>
        <span className="adm-empty-icon" aria-hidden="true"><UiIcon name={icon} size={19} /></span>
        <strong>{title}</strong>
        {description && <span>{description}</span>}
        {action}
    </div>;
}

export function AdminCallout({ tone = "info", icon, children }) {
    const iconName = icon || (tone === "warning" || tone === "danger" ? "warning" : tone === "success" ? "check" : "shield");
    return <div className={`adm-callout${tone === "info" ? "" : ` is-${tone}`}`} role={tone === "danger" ? "alert" : undefined}>
        <UiIcon name={iconName} size={15} />
        <div>{children}</div>
    </div>;
}

export function AdminProgress({ value, tone, label }) {
    const safe = Math.max(0, Math.min(100, Number(value) || 0));
    return <div className={`adm-progress${tone ? ` is-${tone}` : ""}`} role="progressbar" aria-valuemin={0} aria-valuemax={100}
        aria-valuenow={Math.round(safe)} aria-label={label}><span style={{ width: `${safe}%` }} /></div>;
}

function formatPercent(value, digits) {
    const fr = typeof document !== "undefined" && String(document.documentElement?.lang || "").startsWith("fr");
    const text = new Intl.NumberFormat(fr ? "fr-CA" : "en-CA", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
    return fr ? `${text}\u00a0%` : `${text}%`;
}

export function AdminWeight({ value, digits = 1 }) {
    const numeric = Number(value);
    const safe = Number.isFinite(numeric) ? numeric : 0;
    return <span className="adm-weight">
        <span className="adm-weight-bar" aria-hidden="true"><span style={{ width: `${Math.max(0, Math.min(100, safe))}%` }} /></span>
        <span dir="ltr" style={{ unicodeBidi: "isolate" }}>{formatPercent(safe, digits)}</span>
    </span>;
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const DISCARD_COPY = {
    en: { message: "Discard unsaved changes?", detail: "Your edits in this dialog have not been saved.", keep: "Keep editing", discard: "Discard changes" },
    fr: { message: "Abandonner les modifications non enregistrées?", detail: "Vos modifications dans cette fenêtre n’ont pas été enregistrées.", keep: "Continuer la modification", discard: "Abandonner les modifications" },
};

// Accessible modal dialog: Escape closes, focus moves inside and is trapped,
// focus returns to the trigger on close, the header and actions stay visible
// while long content scrolls. When `dirty` is true, every way of closing without
// saving (Escape, backdrop, ×, and Cancel buttons that call `requestClose`)
// first asks the user to confirm discarding their changes.
// `footer` may be a node or a function receiving { requestClose }.
// `managed` marks a dialog whose caller renders its own EN/FR copy, so the page translator skips it.
export function AdminDialog({ title, description, onClose, children, footer, size = "md", footNote, initialFocus = true, closeLabel = "Close", dirty = false, language, managed = false }) {
    const panelRef = useRef(null);
    const keepRef = useRef(null);
    const titleId = useId();
    const descId = useId();
    const discardId = useId();
    const [confirmingDiscard, setConfirmingDiscard] = useState(false);
    const state = useRef({});
    const requestClose = () => {
        if (state.current.dirty) setConfirmingDiscard(true);
        else state.current.onClose?.();
    };
    state.current = { onClose, dirty, confirmingDiscard, requestClose };
    const lang = language || (typeof document !== "undefined" && String(document.documentElement?.lang || "").startsWith("fr") ? "fr" : "en");
    const discardCopy = DISCARD_COPY[lang] || DISCARD_COPY.en;

    useEffect(() => { if (confirmingDiscard) keepRef.current?.focus({ preventScroll: true }); }, [confirmingDiscard]);
    useEffect(() => { if (!dirty) setConfirmingDiscard(false); }, [dirty]);

    useEffect(() => {
        const previouslyFocused = typeof document !== "undefined" ? document.activeElement : null;
        const panel = panelRef.current;
        if (panel && initialFocus) {
            const body = panel.querySelector(".adm-dialog-body");
            const target = body?.querySelector(FOCUSABLE) || panel.querySelector(FOCUSABLE);
            (target || panel).focus({ preventScroll: true });
        }
        const onKeyDown = event => {
            if (event.key === "Escape") {
                event.stopPropagation();
                if (state.current.confirmingDiscard) setConfirmingDiscard(false);
                else state.current.requestClose();
                return;
            }
            if (event.key !== "Tab" || !panelRef.current) return;
            const nodes = [...panelRef.current.querySelectorAll(FOCUSABLE)].filter(node => node.offsetParent !== null || node === document.activeElement);
            if (!nodes.length) return;
            const first = nodes[0];
            const last = nodes[nodes.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        };
        document.addEventListener("keydown", onKeyDown, true);
        return () => {
            document.removeEventListener("keydown", onKeyDown, true);
            if (previouslyFocused && typeof previouslyFocused.focus === "function" && document.contains(previouslyFocused)) previouslyFocused.focus({ preventScroll: true });
        };
    }, [initialFocus]);

    const footerContent = typeof footer === "function" ? footer({ requestClose }) : footer;
    const dialog = <div className="adm-dialog-backdrop" role="presentation" data-cvi-i18n={managed ? "managed" : undefined} lang={managed ? lang : undefined}
        onMouseDown={event => { if (event.target === event.currentTarget) requestClose(); }}>
        <div ref={panelRef} className={`adm-dialog is-${size}`} role="dialog" aria-modal="true" aria-labelledby={titleId}
            aria-describedby={description ? descId : undefined} tabIndex={-1}>
            <div className="adm-dialog-head">
                <div>
                    <h2 className="adm-dialog-title" id={titleId}>{title}</h2>
                    {description && <p className="adm-dialog-desc" id={descId}>{description}</p>}
                </div>
                <button type="button" className="adm-icon-btn" onClick={requestClose} aria-label={closeLabel}><UiIcon name="close" size={16} /></button>
            </div>
            <div className="adm-dialog-body">{children}</div>
            {confirmingDiscard
                ? <div className="adm-dialog-foot adm-discard-bar" role="alertdialog" aria-labelledby={discardId} data-cvi-i18n="managed">
                    <span className="adm-foot-note" id={discardId}><strong>{discardCopy.message}</strong> {discardCopy.detail}</span>
                    <button ref={keepRef} type="button" className="adm-btn" onClick={() => setConfirmingDiscard(false)}>{discardCopy.keep}</button>
                    <button type="button" className="adm-btn adm-btn--danger" onClick={() => { setConfirmingDiscard(false); onClose?.(); }}>{discardCopy.discard}</button>
                </div>
                : footerContent && <div className="adm-dialog-foot">{footNote && <span className="adm-foot-note">{footNote}</span>}{footerContent}</div>}
        </div>
    </div>;
    return typeof document !== "undefined" && document.body ? createPortal(dialog, document.body) : dialog;
}

export function initialsOf(name) {
    const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "?";
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}
